import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Radio, RadioGroup,
  FormControlLabel, FormControl, Checkbox, Grid, IconButton, Divider,
  InputAdornment, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Tooltip, Fade, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Select, MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon, Save as SaveIcon, Clear as ClearIcon,
  CloudDownload, Refresh as RefreshIcon, Add as AddIcon, Close as CloseIcon,
  HealthAndSafety as InsuranceIcon, MedicalServices as ServiceIcon,
  CheckCircle as CheckCircleIcon, Delete as DeleteIcon,   SwapHoriz as MoveIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';


import {
  loadInsurance, loadServices, getServiceInsurances,
  updateInsuredService, deleteInsuredService,
  saveNewService, moveServicesToInsurancedb,
} from '../BaseURL';

/* ─────────────────────────────────────────────────────────────
   Shared compact styles
───────────────────────────────────────────────────────────── */
const cellSelectSx = {
  fontSize: '0.78rem',
  '& .MuiSelect-select': { py: '4px', px: '8px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
};
const cellInputSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.78rem',
    '& input': { py: '4px', px: '8px' },
    '& fieldset': { borderColor: '#e0e0e0' },
    '&:hover fieldset': { borderColor: '#667eea' },
    '&.Mui-focused fieldset': { borderColor: '#667eea' },
  },
};

/* ─────────────────────────────────────────────────────────────
   Editable table row
───────────────────────────────────────────────────────────── */
const EditableRow = ({ row, idx, onChange, onUpdate, onDelete, updating, deleting }) => {
  const tdSx = { borderBottom: '1px solid #f0f0f0', py: 0.75, px: 1 };
  return (
    <TableRow sx={{
      '&:nth-of-type(odd)': { background: '#f9faff' },
      '&:nth-of-type(even)': { background: '#fff' },
      '&:hover': { background: 'rgba(102,126,234,0.04)' },
    }}>
      <TableCell sx={{ ...tdSx, width: 36, textAlign: 'center', color: '#9e9e9e', fontSize: '0.72rem', fontWeight: 500 }}>{idx + 1}</TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 130 }}>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1a237e' }}>{row.STATUS || '—'}</Typography>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 140 }}>
        <Select size="small" fullWidth value={row.charge_status || ''} onChange={e => onChange('charge_status', e.target.value)} displayEmpty sx={cellSelectSx}>
          <MenuItem value="" disabled><em style={{ color: '#bbb', fontSize: '0.75rem' }}>Select…</em></MenuItem>
          <MenuItem value="Auto">Auto Services</MenuItem>
          <MenuItem value="Main Services">Main Services</MenuItem>
          <MenuItem value="Manual">Manual Services</MenuItem>
        </Select>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 100 }}>
        <TextField size="small" type="number" fullWidth value={row.amo ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('amo', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 95 }}>
        <TextField size="small" type="number" fullWidth value={row.toup ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('toup', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 100 }}>
        <Select size="small" fullWidth value={row.age || ''} onChange={e => onChange('age', e.target.value)} displayEmpty sx={cellSelectSx}>
          <MenuItem value="" disabled><em style={{ color: '#bbb', fontSize: '0.75rem' }}>Select…</em></MenuItem>
          <MenuItem value="Adult">Adult</MenuItem>
          <MenuItem value="Child">Child</MenuItem>
          <MenuItem value="Both">Both</MenuItem>
        </Select>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" fullWidth value={row.code ?? ''} onChange={e => onChange('code', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 180, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
          <Button size="small" variant="contained" onClick={() => onUpdate(row)} disabled={updating || deleting}
            startIcon={updating ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            sx={{ fontSize: '0.7rem', px: 1.5, py: 0.5, background: 'linear-gradient(90deg,#11998e,#11998e)', color: 'white', fontWeight: 600, textTransform: 'none', boxShadow: '0 2px 8px rgba(17,153,142,0.25)', '&:hover': { background: 'linear-gradient(90deg,#0e8a7f,#2fd469)' }, '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' } }}>
            {updating ? 'Saving...' : 'Update'}
          </Button>
          <Button size="small" variant="contained" onClick={() => onDelete(row)} disabled={deleting || updating}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
            sx={{ fontSize: '0.7rem', px: 1.5, py: 0.5, background: 'linear-gradient(90deg,#e53935,#e53935)', color: 'white', fontWeight: 600, textTransform: 'none', boxShadow: '0 2px 8px rgba(229,57,53,0.25)', '&:hover': { background: 'linear-gradient(90deg,#c62828,#ef5350)' }, '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' } }}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
};

/* ─────────────────────────────────────────────────────────────
   New-Service Dialog
───────────────────────────────────────────────────────────── */
const NewServiceDialog = ({ open, onClose, insuranceProviders, loadingInsurance, onSaved }) => {
  const [saving, setSaving] = useState(false);

  // ── form state ────────────────────────────────────────────
  const [chargeStatus, setChargeStatus]                     = useState('');
  const [ageStatus, setAgeStatus]                           = useState('');
  const [serviceName, setServiceName]                       = useState('');
  const [insuranceTariff, setInsuranceTariff]               = useState('');
  const [topUp, setTopUp]                                   = useState('');
  const [specialityDescription, setSpecialityDescription]   = useState('');
  const [specialityCode, setSpecialityCode]                 = useState('');
  const [selectedInsurances, setSelectedInsurances]         = useState({});
  const [insuranceSearch, setInsuranceSearch]               = useState('');
  const [errors, setErrors]                                 = useState({});

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;

  const clearError = (field) => setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

  const handleInsuranceToggle = (id) => {
    setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
    clearError('insurance');
  };
  const handleSelectAll = () => {
    const all = {};
    insuranceProviders.forEach(i => { all[i.id] = true; });
    setSelectedInsurances(all);
    clearError('insurance');
  };
  const handleDeselectAll = () => setSelectedInsurances({});

  /* ── validation ──────────────────────────────────────────── */
  const validate = () => {
    const e = {};

    if (!chargeStatus)
      e.chargeStatus = 'Charge Status is required';

    if (!ageStatus)
      e.ageStatus = 'Age Status is required';

    if (!serviceName.trim())
      e.serviceName = 'Service Name is required';

    // Tariff: required, must be a number ≥ 0 (0 is allowed)
    if (insuranceTariff === '' || insuranceTariff === null || insuranceTariff === undefined)
      e.insuranceTariff = 'Insurance Tariff is required';
    else if (isNaN(parseFloat(insuranceTariff)) || parseFloat(insuranceTariff) < 0)
      e.insuranceTariff = 'Must be a valid number (0 or more)';

    // Top-up: same rule
    if (topUp === '' || topUp === null || topUp === undefined)
      e.topUp = 'Top-up is required';
    else if (isNaN(parseFloat(topUp)) || parseFloat(topUp) < 0)
      e.topUp = 'Must be a valid number (0 or more)';

    if (!specialityCode.trim())
      e.specialityCode = 'Speciality Code is required';

    if (selectedCount === 0)
      e.insurance = 'At least one insurance provider must be selected';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── save handler ────────────────────────────────────────── */
  const handleSave = async () => {
    if (!validate()) {
      // Collect human-readable messages for the alert
      const pending = {
        ...(!chargeStatus                                                             ? { a: 'Charge Status must be selected'              } : {}),
        ...(!ageStatus                                                                ? { b: 'Age Status must be selected'                 } : {}),
        ...(!serviceName.trim()                                                       ? { c: 'Service Name must be entered'                } : {}),
        ...(insuranceTariff === ''                                                    ? { d: 'Insurance Tariff must be entered'            } : {}),
        ...(topUp === ''                                                              ? { e: 'Top-up must be entered'                     } : {}),
        ...(!specialityCode.trim()                                                    ? { f: 'Speciality Code must be entered'             } : {}),
        ...(selectedCount === 0                                                       ? { g: 'At least one insurance must be selected'    } : {}),
      };
      await Swal.fire({
        title: 'Please complete all required fields',
        html: `<ul style="text-align:left;margin:0;padding-left:1.2em;">${Object.values(pending).map(m => `<li>${m}</li>`).join('')}</ul>`,
        icon: 'warning',
        confirmButtonColor: '#11998e',
      });
      return;
    }

    setSaving(true);
    try {
      /*
       * One row per selected insurance → one INSERT per row in insured_services
       *   charge_status  ← chargeStatus
       *   age            ← ageStatus
       *   service        ← serviceName
       *   amo            ← insuranceTariff
       *   toup           ← topUp
       *   code           ← specialityCode
       *   STATUS         ← insurance provider name (the label column)
       
       */
      const rows = selectedInsuranceIds.map(id => {
        const provider = insuranceProviders.find(p => String(p.id) === String(id));
        return {
          charge_status:           chargeStatus,
          age:                     ageStatus,
          service:                 serviceName.trim(),
          amo:                     parseFloat(insuranceTariff),
          toup:                    parseFloat(topUp),
          code:                    specialityCode.trim(),
          speciality_description:  specialityDescription.trim(),
          STATUS:                  provider?.name || ''
        };
      });

      const response = await axios.post(saveNewService, { rows });

      if (response.data.success) {
        await Swal.fire({
          title: 'Saved!',
          html: `<b>${serviceName}</b> saved with <b>${selectedCount}</b> insurance record${selectedCount !== 1 ? 's' : ''}.`,
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
          timerProgressBar: true,
        });
        if (onSaved) onSaved();
        handleClose();
      } else {
        throw new Error(response.data.message || 'Save failed');
      }
    } catch (err) {
      await Swal.fire({
        title: 'Save Failed',
        text: err.response?.data?.message || err.message || 'An error occurred while saving',
        icon: 'error',
        confirmButtonColor: '#e53935',
      });
    } finally {
      setSaving(false);
    }
  };

  /* ── reset & close ───────────────────────────────────────── */
  const handleClose = () => {
    setChargeStatus(''); setAgeStatus(''); setServiceName('');
    setInsuranceTariff(''); setTopUp('');
    setSpecialityDescription(''); setSpecialityCode('');
    setSelectedInsurances({}); setInsuranceSearch('');
    setErrors({});
    onClose();
  };

  const filteredProviders = insuranceProviders.filter(i =>
    i.name?.toLowerCase().includes(insuranceSearch.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' } }}>

      <DialogTitle sx={{ background: 'linear-gradient(135deg,#11998e,#38ef7d)', color: 'white', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ServiceIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>New Service</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>Configure service details and assign insurance providers</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Grid container sx={{ minHeight: 520 }} columns={{ xs: 4, sm: 8, md: 12 }}>

          {/* ── LEFT: service fields ───────────────────────── */}
          <Grid item xs={12} md={6} size={6} sx={{ p: 3, borderRight: { md: '1px solid #e8eaf6' }, background: '#fafbff' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3949ab', mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
              Service Details
            </Typography>

            {/* Charge Status */}
            <FormControl component="fieldset" sx={{ mb: 1.5, width: '100%' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: errors.chargeStatus ? '#e53935' : '#555', mb: 0.5, display: 'block' }}>
                Charge Status *
              </Typography>
              <RadioGroup row value={chargeStatus}
                onChange={e => { setChargeStatus(e.target.value); clearError('chargeStatus'); }}>
                {[{ v: 'Auto', l: 'Auto' }, { v: 'Main Services', l: 'Main Services' }, { v: 'Manual', l: 'Manual' }].map(({ v, l }) => (
                  <FormControlLabel key={v} value={v}
                    control={<Radio size="small" sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
                    label={<Typography variant="body2">{l}</Typography>} />
                ))}
              </RadioGroup>
              {errors.chargeStatus && <Typography variant="caption" sx={{ color: '#e53935', fontSize: '0.7rem' }}>{errors.chargeStatus}</Typography>}
            </FormControl>

            {/* Age Status */}
            <FormControl component="fieldset" sx={{ mb: 1.5, width: '100%' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: errors.ageStatus ? '#e53935' : '#555', mb: 0.5, display: 'block' }}>
                Age Status *
              </Typography>
              <RadioGroup row value={ageStatus}
                onChange={e => { setAgeStatus(e.target.value); clearError('ageStatus'); }}>
                {['Adult', 'Child', 'Both'].map(v => (
                  <FormControlLabel key={v} value={v}
                    control={<Radio size="small" sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
                    label={<Typography variant="body2">{v}</Typography>} />
                ))}
              </RadioGroup>
              {errors.ageStatus && <Typography variant="caption" sx={{ color: '#e53935', fontSize: '0.7rem' }}>{errors.ageStatus}</Typography>}
            </FormControl>

            <Divider sx={{ my: 1.5 }} />

            {/* Service Name */}
            <TextField fullWidth size="small" label="Service Name *" value={serviceName}
              onChange={e => { setServiceName(e.target.value); clearError('serviceName'); }}
              error={!!errors.serviceName} helperText={errors.serviceName} sx={{ mb: 2 }} />

            {/* Tariff + Top-up */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6} size={6}>
                <TextField fullWidth size="small" label="Insurance Tariff GH¢ *" type="number"
                  value={insuranceTariff}
                  onChange={e => { setInsuranceTariff(e.target.value); clearError('insuranceTariff'); }}
                  error={!!errors.insuranceTariff} helperText={errors.insuranceTariff}
                  inputProps={{ min: 0, step: '0.01' }} />
              </Grid>
              <Grid item xs={12} md={6} size={6}>
                <TextField fullWidth size="small" label="Top-up *" type="number"
                  value={topUp}
                  onChange={e => { setTopUp(e.target.value); clearError('topUp'); }}
                  error={!!errors.topUp} helperText={errors.topUp}
                  inputProps={{ min: 0, step: '0.01' }} />
              </Grid>
            </Grid>

            {/* Speciality Description (optional) */}
            <TextField fullWidth size="small" label="Speciality Description" value={specialityDescription}
              onChange={e => setSpecialityDescription(e.target.value)} sx={{ mb: 2 }} />

            {/* Speciality Code */}
            <TextField fullWidth size="small" label="Speciality Code *" value={specialityCode}
              onChange={e => { setSpecialityCode(e.target.value); clearError('specialityCode'); }}
              error={!!errors.specialityCode} helperText={errors.specialityCode} />
          </Grid>

          {/* ── RIGHT: insurance checkboxes ────────────────── */}
          <Grid item xs={12} md={6} size={6} sx={{ p: 3, display: 'flex', flexDirection: 'column', background: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box>
                <Typography variant="subtitle2"
                  sx={{ fontWeight: 700, color: errors.insurance ? '#e53935' : '#3949ab', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
                  Insurance Providers *
                </Typography>
                <Typography variant="caption" color={errors.insurance ? 'error' : 'text.secondary'}>
                  {selectedCount} of {insuranceProviders.length} selected
                </Typography>
              </Box>
              {selectedCount > 0 && (
                <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                  label={`${selectedCount} selected`}
                  sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }} />
              )}
            </Box>

            {errors.insurance && (
              <Typography variant="caption" sx={{ color: '#e53935', fontSize: '0.7rem', mb: 0.5 }}>
                {errors.insurance}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, mt: 0.5 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll}
                sx={{ borderColor: '#667eea', color: '#667eea', fontSize: '0.72rem', py: 0.4 }}>
                Select All
              </Button>
              <Button size="small" variant="outlined" onClick={handleDeselectAll}
                sx={{ borderColor: '#667eea', color: '#667eea', fontSize: '0.72rem', py: 0.4 }}>
                Deselect All
              </Button>
            </Box>

            <TextField fullWidth size="small" placeholder="Search insurance..."
              value={insuranceSearch} onChange={e => setInsuranceSearch(e.target.value)} sx={{ mb: 1.5 }}
              InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: '#9e9e9e' }} /></InputAdornment>) }} />

            <Box sx={{
              flex: 1, overflowY: 'auto',
              border: `1px solid ${errors.insurance ? '#e53935' : '#e8eaf6'}`,
              borderRadius: 2, p: 1, minHeight: 280, maxHeight: 320,
              transition: 'border-color 0.2s',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { background: '#c5cae9', borderRadius: '10px' },
            }}>
              {loadingInsurance ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={28} />
                </Box>
              ) : filteredProviders.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">No providers found</Typography>
                </Box>
              ) : (
                filteredProviders.map(insurance => (
                  <Box key={insurance.id} onClick={() => handleInsuranceToggle(insurance.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1,
                      cursor: 'pointer', mb: 0.5,
                      background: selectedInsurances[insurance.id] ? 'rgba(102,126,234,0.08)' : 'transparent',
                      border: selectedInsurances[insurance.id] ? '1px solid rgba(102,126,234,0.25)' : '1px solid transparent',
                      transition: 'all 0.15s ease',
                      '&:hover': { background: 'rgba(102,126,234,0.06)' },
                    }}>
                    <Checkbox size="small"
                      checked={selectedInsurances[insurance.id] || false}
                      onChange={() => handleInsuranceToggle(insurance.id)}
                      onClick={e => e.stopPropagation()}
                      sx={{ p: 0.5, color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />
                    <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem', color: '#333' }}>
                      {insurance.name}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0', background: '#fafafa', gap: 1 }}>
        <Button onClick={handleClose} startIcon={<ClearIcon />} variant="outlined" disabled={saving}
          sx={{ color: '#757575', borderColor: '#e0e0e0', '&:hover': { background: '#f5f5f5' } }}>
          Cancel
        </Button>
        <Button variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave} disabled={saving}
          sx={{
            background: 'linear-gradient(90deg,#11998e,#38ef7d)', color: 'white', fontWeight: 700, px: 3,
            boxShadow: '0 4px 14px rgba(17,153,142,0.35)',
            '&:hover': { background: 'linear-gradient(90deg,#0e8a7f,#2fd469)', boxShadow: '0 6px 18px rgba(17,153,142,0.45)' },
            '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' },
          }}>
          {saving ? 'Saving...' : 'Save Service'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─────────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────────── */
const ServicesSetting = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [loadingInsurance, setLoadingInsurance] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingServiceInsurances, setLoadingServiceInsurances] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState(null);
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceInsuranceRows, setServiceInsuranceRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [insuranceFilter, setInsuranceFilter] = useState('');

  // ── Move services state ───────────────────────────────────
const [selectedServiceNames, setSelectedServiceNames] = useState(new Set());
const [moveDialogOpen, setMoveDialogOpen]             = useState(false);
const [moveTargetInsurance, setMoveTargetInsurance]   = useState('');
const [moving, setMoving]                             = useState(false);
const [selectMode, setSelectMode]                     = useState(false);

  const loadInsuranceProviders = async () => {
    setLoadingInsurance(true); setError(null);
    try {
      const res = await axios.get(loadInsurance);
      if (res.data.success) setInsuranceProviders(res.data.results);
      else setError('Failed to load insurance providers');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load insurance providers');
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load insurance providers', icon: 'error' });
    } finally { setLoadingInsurance(false); }
  };

  const loadServicesFromBackend = async () => {
    setLoadingServices(true); setError(null);
    try {
      const res = await axios.get(loadServices);
      if (res.data.success) setServices(res.data.results);
      else setError('Failed to load services');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load services');
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load services', icon: 'error' });
    } finally { setLoadingServices(false); }
  };

  const loadServiceInsurancesForDisplay = async (serviceId, serviceName) => {
    setLoadingServiceInsurances(true); setServiceInsuranceRows([]);
    try {
      const res = await axios.post(getServiceInsurances, { serviceId, serviceName });
      if (res.data.success) setServiceInsuranceRows(res.data.results);
    } catch (err) {
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load service insurances', icon: 'error' });
    } finally { setLoadingServiceInsurances(false); }
  };

  const handleUpdateRow = async (row) => {
    if (!row.charge_status || !row.amo || !row.toup || !row.age) {
      await Swal.fire({ title: 'Validation Error', text: 'Please fill in all required fields (Charge Status, Tariff, Top-up, Age)', icon: 'warning' }); return;
    }
    if (!row.id) { await Swal.fire({ title: 'Error', text: 'Record ID is missing. Cannot update.', icon: 'error' }); return; }
    setUpdatingRowId(row.id);
    try {
      const res = await axios.post(updateInsuredService, { id: row.id, charge_status: row.charge_status, amo: parseFloat(row.amo), toup: parseFloat(row.toup), age: row.age, code: row.code || '' });
      if (res.data.success) {
        await Swal.fire({ title: 'Success!', text: `Successfully updated ${row.STATUS}`, icon: 'success', timer: 2000, showConfirmButton: false });
        if (selectedService) await loadServiceInsurancesForDisplay(selectedService.id, selectedService.service);
      } else throw new Error(res.data.message || 'Update failed');
    } catch (err) {
      await Swal.fire({ title: 'Update Failed', text: err.response?.data?.message || err.message || 'Failed to update', icon: 'error' });
    } finally { setUpdatingRowId(null); }
  };

  const handleDeleteRow = async (row) => {
    if (!row.id) { await Swal.fire({ title: 'Error', text: 'Record ID is missing. Cannot delete.', icon: 'error' }); return; }
    const result = await Swal.fire({
      title: 'Confirm Delete',
      html: `Are you sure you want to delete the record for <strong>${row.STATUS || 'this entry'}</strong>?<br/><span style="color:#e53935;font-size:0.9rem;">This action cannot be undone.</span>`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#e53935', cancelButtonColor: '#9e9e9e',
      confirmButtonText: 'Yes, Delete', cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    setDeletingRowId(row.id);
    try {
      const res = await axios.post(deleteInsuredService, { id: row.id });
      if (res.data.success) {
        await Swal.fire({ title: 'Deleted!', text: `Record for "${row.STATUS}" has been deleted.`, icon: 'success', timer: 2000, showConfirmButton: false });
        setServiceInsuranceRows(prev => prev.filter(r => r.id !== row.id));
      } else throw new Error(res.data.message || 'Delete failed');
    } catch (err) {
      await Swal.fire({ title: 'Delete Failed', text: err.response?.data?.message || err.message || 'Failed to delete', icon: 'error' });
    } finally { setDeletingRowId(null); }
  };

  useEffect(() => { loadInsuranceProviders(); loadServicesFromBackend(); }, []);

  const handleServiceClick = async (service) => {
    if (selectedService?.id === service.id) { setSelectedService(null); setServiceInsuranceRows([]); return; }
    setSelectedService(service);
    await loadServiceInsurancesForDisplay(service.id, service.service);
  };

  const handleRowChange = (idx, field, value) =>
    setServiceInsuranceRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));



  const handleMoveServices = async () => {
  if (selectedServiceNames.size === 0 || !moveTargetInsurance) return;
  setMoving(true);
  try {
    const res = await axios.post(moveServicesToInsurancedb, {
      serviceNames:    Array.from(selectedServiceNames),
      targetInsurance: moveTargetInsurance,
    });
    if (res.data.success) {
      await Swal.fire({
        title: 'Done!',
        text: res.data.message,
        icon: 'success',
        timer: 3000,
        showConfirmButton: false,
        timerProgressBar: true,
      });
      setMoveDialogOpen(false);
      setSelectedServiceNames(new Set());
      setMoveTargetInsurance('');
      setSelectMode(false);
      loadServicesFromBackend();
    } else {
      throw new Error(res.data.message);
    }
  } catch (err) {
    await Swal.fire({
      title: 'Move Failed',
      text: err.response?.data?.message || err.message,
      icon: 'error',
    });
  } finally {
    setMoving(false);
  }
};

const toggleServiceSelection = (serviceName) => {
  setSelectedServiceNames(prev => {
    const next = new Set(prev);
    next.has(serviceName) ? next.delete(serviceName) : next.add(serviceName);
    return next;
  });
};

const handleSelectAllVisible = (visibleServices) => {
  setSelectedServiceNames(new Set(visibleServices.map(s => s.service)));
};

const handleDeselectAll = () => setSelectedServiceNames(new Set());

  return (
    <>
      <Paper elevation={8} sx={{ margin: '0 auto', borderRadius: 3, overflow: 'hidden', background: '#ffffff' }}>
        {/* Header */}
        <Box sx={{ background: 'linear-gradient(135deg,#11998e,#38ef7d)', color: 'white', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '0.5px', textShadow: '1px 2px 4px rgba(0,0,0,0.15)' }}>
            Insured / Noninsured Service Information
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="contained" onClick={loadServicesFromBackend} disabled={loadingServices}
              startIcon={loadingServices ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              size="small" sx={{ background: 'rgba(255,255,255,0.2)', '&:hover': { background: 'rgba(255,255,255,0.3)' } }}>
              {loadingServices ? 'Loading...' : 'Reload Services'}
            </Button>
            <Button variant="contained" onClick={loadInsuranceProviders} disabled={loadingInsurance}
              startIcon={loadingInsurance ? <CircularProgress size={16} color="inherit" /> : <CloudDownload />}
              size="small" sx={{ background: 'rgba(255,255,255,0.2)', '&:hover': { background: 'rgba(255,255,255,0.3)' } }}>
              {loadingInsurance ? 'Loading...' : 'Reload Insurance'}
            </Button>
            <Tooltip title="Add a new service">
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)} size="small"
                sx={{ background: 'rgba(255,255,255,0.95)', color: '#11998e', fontWeight: 700, '&:hover': { background: '#fff', boxShadow: '0 4px 14px rgba(0,0,0,0.2)' } }}>
                New Service
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
          <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>

            {/* Left: service list */}
            <Grid item xs={12} md={4} size={4}>
             <Paper elevation={0} sx={{ p: 2.5, border: '2px solid #e0e0e0', borderRadius: 2, background: 'linear-gradient(to bottom,#fff8f3,#ffffff)', height: 580, display: 'flex', flexDirection: 'column' }}>

  {/* Insurance filter */}
  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
    <Select
      displayEmpty
      value={insuranceFilter}
      onChange={e => setInsuranceFilter(e.target.value)}
      renderValue={selected => {
        if (!selected) return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#9e9e9e' }}>
            <InsuranceIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>Filter by Insurance…</Typography>
          </Box>
        );
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InsuranceIcon sx={{ fontSize: 16, color: '#667eea' }} />
            <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#3949ab', fontWeight: 600 }}>{selected}</Typography>
          </Box>
        );
      }}
      sx={{
        fontSize: '0.82rem',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: insuranceFilter ? '#667eea' : '#e0e0e0' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
      }}
    >
      <MenuItem value=""><em style={{ color: '#9e9e9e', fontSize: '0.82rem' }}>All Insurances</em></MenuItem>
      {insuranceProviders.map(ins => (
        <MenuItem key={ins.id} value={ins.name}>
          <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{ins.name}</Typography>
        </MenuItem>
      ))}
    </Select>
  </FormControl>

  {insuranceFilter && (
    <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Chip size="small" icon={<InsuranceIcon sx={{ fontSize: '13px !important' }} />}
        label={insuranceFilter} onDelete={() => setInsuranceFilter('')}
        sx={{ fontSize: '0.72rem', background: 'rgba(102,126,234,0.1)', color: '#3949ab', fontWeight: 600, height: 22 }} />
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>— filtering services</Typography>
    </Box>
  )}

  <TextField fullWidth size="small" placeholder="Search services..." value={searchTerm}
    onChange={e => setSearchTerm(e.target.value)} sx={{ mb: 1.5 }}
    InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: '#667eea' }} /></InputAdornment>) }} />

  {/* ── Move toolbar ── */}
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', fontSize: '0.8rem' }}>
        Available Services
      </Typography>
      {loadingServices && <CircularProgress size={14} />}
      <Chip size="small" label={`${services.length} total`}
        sx={{ fontSize: '0.68rem', background: '#e8eaf6', color: '#3949ab' }} />
    </Box>
    <Tooltip title={selectMode ? 'Exit selection mode' : 'Select services to move'}>
      <Button size="small" variant={selectMode ? 'contained' : 'outlined'}
        startIcon={<MoveIcon sx={{ fontSize: 14 }} />}
        onClick={() => { setSelectMode(s => !s); setSelectedServiceNames(new Set()); }}
        sx={{
          fontSize: '0.68rem', py: 0.3, px: 1,
          borderColor: '#667eea', color: selectMode ? 'white' : '#667eea',
          bgcolor: selectMode ? '#667eea' : 'transparent',
          '&:hover': { bgcolor: selectMode ? '#5c6bc0' : 'rgba(102,126,234,0.06)' }
        }}>
        {selectMode ? 'Cancel' : 'Move'}
      </Button>
    </Tooltip>
  </Box>

  {/* ── Select all / Move action bar ── */}
  {selectMode && (
    <Box sx={{ mb: 1, p: 1, borderRadius: 1.5, bgcolor: 'rgba(102,126,234,0.06)', border: '1px solid rgba(102,126,234,0.2)', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#3949ab', fontSize: '0.72rem' }}>
        {selectedServiceNames.size} selected
      </Typography>
      <Button size="small" variant="text"
        onClick={() => handleSelectAllVisible(
          services
            .filter(s => s.service?.toLowerCase().includes(searchTerm.toLowerCase()))
            .filter(s => !insuranceFilter || s.insurances?.some(i => i.name === insuranceFilter))
        )}
        sx={{ fontSize: '0.68rem', py: 0.2, color: '#667eea', minWidth: 0 }}>
        All
      </Button>
      <Button size="small" variant="text" onClick={handleDeselectAll}
        sx={{ fontSize: '0.68rem', py: 0.2, color: '#9e9e9e', minWidth: 0 }}>
        None
      </Button>
      {selectedServiceNames.size > 0 && (
        <Button size="small" variant="contained"
          startIcon={<MoveIcon sx={{ fontSize: 13 }} />}
          onClick={() => setMoveDialogOpen(true)}
          sx={{ ml: 'auto', fontSize: '0.68rem', py: 0.3, px: 1.2, bgcolor: '#667eea', '&:hover': { bgcolor: '#5c6bc0' } }}>
          Move {selectedServiceNames.size}
        </Button>
      )}
    </Box>
  )}

  {selectedService && !selectMode && (
    <Box sx={{ mb: 1.5, p: 1, borderRadius: 1.5, background: 'linear-gradient(90deg,rgba(102,126,234,0.1),rgba(56,239,125,0.08))', border: '1px solid rgba(102,126,234,0.2)', display: 'flex', alignItems: 'center', gap: 1 }}>
      <CheckCircleIcon sx={{ fontSize: 16, color: '#11998e' }} />
      <Typography variant="caption" sx={{ color: '#3949ab', fontWeight: 600 }}>Selected: {selectedService.service}</Typography>
      <IconButton size="small" onClick={() => { setSelectedService(null); setServiceInsuranceRows([]); }} sx={{ ml: 'auto', p: 0.2 }}>
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  )}

  <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#c5cae9', borderRadius: '10px' } }}>
    {loadingServices ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>
    ) : services.length === 0 ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 1 }}>
        <ServiceIcon sx={{ fontSize: 40, color: '#bdbdbd' }} />
        <Typography variant="body2" color="text.secondary">No services found.</Typography>
      </Box>
    ) : (
      services
        .filter(s => s.service?.toLowerCase().includes(searchTerm.toLowerCase()))
        .filter(s => !insuranceFilter || s.insurances?.some(i => i.name === insuranceFilter))
        .map(service => {
          const isChecked = selectedServiceNames.has(service.service);
          const isActive  = selectedService?.id === service.id && !selectMode;
          return (
            <Box key={service.id}
              onClick={() => selectMode ? toggleServiceSelection(service.service) : handleServiceClick(service)}
              sx={{
                display: 'flex', alignItems: 'center', mb: 0.75, p: 1.25, borderRadius: 1.5,
                cursor: 'pointer', transition: 'all 0.18s ease',
                border: isActive ? '1.5px solid #667eea' : isChecked ? '1.5px solid #11998e' : '1.5px solid transparent',
                background: isActive ? 'rgba(102,126,234,0.08)' : isChecked ? 'rgba(17,153,142,0.07)' : 'transparent',
                '&:hover': { background: 'rgba(102,126,234,0.05)', transform: 'translateX(3px)', borderColor: 'rgba(102,126,234,0.3)' }
              }}>
              {selectMode ? (
                <Checkbox size="small" checked={isChecked}
                  onChange={() => toggleServiceSelection(service.service)}
                  onClick={e => e.stopPropagation()}
                  sx={{ p: 0.3, color: '#667eea', '&.Mui-checked': { color: '#11998e' }, mr: 0.5 }} />
              ) : (
                <ServiceIcon sx={{ fontSize: 16, mr: 1, color: isActive ? '#667eea' : '#bdbdbd', transition: 'color 0.18s' }} />
              )}
              <Typography variant="body2" sx={{ fontSize: '0.85rem', color: isActive ? '#3949ab' : '#424242', fontWeight: isActive || isChecked ? 700 : 400 }}>
                {service.service}
              </Typography>
              {isChecked && selectMode && (
                <CheckCircleIcon sx={{ fontSize: 14, color: '#11998e', ml: 'auto' }} />
              )}
            </Box>
          );
        })
    )}
  </Box>
</Paper>
            </Grid>

            {/* Right: editable records table */}
            <Grid item xs={12} md={8} size={8}>
              <Paper elevation={0} sx={{ border: '2px solid #e0e0e0', borderRadius: 2, background: '#fff', height: 580, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #e8eaf6', background: '#f8f9ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e' }}>Insured Services Records</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedService ? `Records for: ${selectedService.service}` : 'Select a service to load records'}
                    </Typography>
                  </Box>
                  {selectedService && !loadingServiceInsurances && (
                    <Chip icon={<InsuranceIcon sx={{ fontSize: '14px !important' }} />}
                      label={`${serviceInsuranceRows.length} record${serviceInsuranceRows.length !== 1 ? 's' : ''}`} size="small"
                      sx={{ background: serviceInsuranceRows.length > 0 ? '#e8f5e9' : '#fce4ec', color: serviceInsuranceRows.length > 0 ? '#2e7d32' : '#c62828', fontWeight: 600, fontSize: '0.7rem' }} />
                  )}
                </Box>

                {!selectedService ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, opacity: 0.4 }}>
                    <InsuranceIcon sx={{ fontSize: 56, color: '#90caf9' }} />
                    <Typography variant="body2" color="text.secondary" textAlign="center">Click any service on the left<br />to load its insured records</Typography>
                  </Box>
                ) : loadingServiceInsurances ? (
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box textAlign="center">
                      <CircularProgress size={36} sx={{ color: '#667eea' }} />
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#9e9e9e' }}>Loading records...</Typography>
                    </Box>
                  </Box>
                ) : serviceInsuranceRows.length === 0 ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, opacity: 0.5 }}>
                    <InsuranceIcon sx={{ fontSize: 44, color: '#ef9a9a' }} />
                    <Typography variant="body2" color="text.secondary">No insured records found for this service.</Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ flex: 1, overflowY: 'auto', overflowX: 'auto', '&::-webkit-scrollbar': { width: '6px', height: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#c5cae9', borderRadius: '10px' } }}>
                    <Table size="small" stickyHeader sx={{ minWidth: 860 }}>
                      <TableHead>
                        <TableRow>
                          {['#', 'Insurance', 'Charge Status', 'Tariff (GH¢)', 'Top-up', 'Age', 'Code', 'Actions'].map(label => (
                            <TableCell key={label} sx={{ background: '#11998e', color: '#fff', fontWeight: 600, fontSize: '0.72rem', letterSpacing: '0.3px', borderBottom: 'none', py: 1.2, whiteSpace: 'nowrap', ...(label === '#' && { width: 36, textAlign: 'center' }), ...(label === 'Actions' && { width: 180, textAlign: 'center' }) }}>
                              {label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {serviceInsuranceRows.map((row, idx) => (
                          <EditableRow key={row.id || idx} row={row} idx={idx}
                            onChange={(field, value) => handleRowChange(idx, field, value)}
                            onUpdate={handleUpdateRow} onDelete={handleDeleteRow}
                            updating={updatingRowId === row.id} deleting={deletingRowId === row.id} />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* ══════════ Move Services Dialog ══════════ */}
<Dialog open={moveDialogOpen} onClose={() => !moving && setMoveDialogOpen(false)}
  maxWidth="xs" fullWidth TransitionComponent={Fade}
  PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>

  <DialogTitle sx={{ p: 0 }}>
    <Box sx={{ background: 'linear-gradient(135deg,#667eea,#764ba2)', color: 'white', px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <MoveIcon sx={{ fontSize: 26 }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>Move Services</Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {selectedServiceNames.size} service{selectedServiceNames.size !== 1 ? 's' : ''} selected
          </Typography>
        </Box>
      </Box>
      <IconButton onClick={() => setMoveDialogOpen(false)} disabled={moving} sx={{ color: 'white' }}>
        <CloseIcon />
      </IconButton>
    </Box>
  </DialogTitle>

  <DialogContent sx={{ pt: 3, pb: 1 }}>
    {/* Selected services summary */}
    <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 2, bgcolor: '#f8f9ff', border: '1px solid #e8eaf6', maxHeight: 140, overflowY: 'auto' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#3949ab', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem', display: 'block', mb: 1 }}>
        Services to copy:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {Array.from(selectedServiceNames).map(name => (
          <Chip key={name} label={name} size="small"
            sx={{ fontSize: '0.72rem', height: 20, bgcolor: 'rgba(102,126,234,0.1)', color: '#3949ab' }} />
        ))}
      </Box>
    </Box>

    {/* Target insurance picker */}
    <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', display: 'block', mb: 0.75 }}>
      Move to Insurance *
    </Typography>
    <FormControl fullWidth size="small">
      <Select displayEmpty value={moveTargetInsurance}
        onChange={e => setMoveTargetInsurance(e.target.value)}
        renderValue={v => v
          ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InsuranceIcon sx={{ fontSize: 16, color: '#667eea' }} />
              <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#3949ab' }}>{v}</Typography>
            </Box>
          : <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#9e9e9e' }}>Select target insurance…</Typography>
        }
        sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: moveTargetInsurance ? '#667eea' : '#e0e0e0' } }}>
        {insuranceProviders.map(ins => (
          <MenuItem key={ins.id} value={ins.name}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InsuranceIcon sx={{ fontSize: 15, color: '#667eea' }} />
              <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{ins.name}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem', py: 0.5 }}>
      Services already assigned to the target insurance will be skipped.
    </Alert>
  </DialogContent>

  <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid #f0f0f0' }}>
    <Button onClick={() => setMoveDialogOpen(false)} disabled={moving} variant="outlined"
      sx={{ color: '#757575', borderColor: '#e0e0e0', flex: 1 }}>
      Cancel
    </Button>
    <Button variant="contained" onClick={handleMoveServices}
      disabled={moving || !moveTargetInsurance}
      startIcon={moving ? <CircularProgress size={16} color="inherit" /> : <MoveIcon />}
      sx={{ flex: 1, background: 'linear-gradient(90deg,#667eea,#764ba2)', fontWeight: 700, '&:hover': { filter: 'brightness(0.9)' }, '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' } }}>
      {moving ? 'Moving…' : `Move ${selectedServiceNames.size}`}
    </Button>
  </DialogActions>
</Dialog>


      <NewServiceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        insuranceProviders={insuranceProviders}
        loadingInsurance={loadingInsurance}
        onSaved={loadServicesFromBackend}
      />
    </>
  );
};

export default ServicesSetting;